import React, { useState } from 'react';
import {
  CloseCircleOutlined,
  EditableOutlined,
  TickMarkOutlined,
  WarningFilled,
} from '@duxton-web/icons';
import {
  Button,
  Collapse,
  Input,
  Modal,
  Tag,
  theme,
  Tooltip,
  Typography,
} from '@duxton-web/ui';

import './tag.css';

export interface SubCheck {
  label: string;
  value?: string;
  threshold?: string;
  passed: boolean;
  note?: string;
  description?: string;
  waivable?: boolean;
  action?: () => void;
}

export interface StatusFactor {
  key?: string;
  label: string;
  passed: boolean;
  waivable?: boolean;
  subChecks: SubCheck[];
  action?: () => void;
  fixWarning?: string;
  learnMoreLink?: string;
  description?: string;
}

export interface StatusTagConfig {
  tagLabel: string;
  passLabel: string;
  failLabel: string;
  docLink: string;
  slackLink: string;
  slackLabel: string;
  footerNote: string;
  reduceLink?: string;
  failColor?: 'warning' | 'error';
}

const SubCheckRow = ({
  check,
  token,
  showTag,
  onEditStart,
  editing,
  draft,
  setDraft,
  justification,
  onEditEnd,
  onJustify,
  isWaived,
  factorTestId,
  onClose,
}: {
  check: SubCheck;
  token: ReturnType<typeof theme.getDesignToken>;
  showTag: boolean;
  onEditStart: () => void;
  editing: boolean;
  draft: string;
  setDraft: (val: string) => void;
  justification?: string;
  onEditEnd: () => void;
  onJustify?: (text: string) => void;
  isWaived: boolean;
  factorTestId: string;
  onClose: () => void;
}) => {
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Typography.Text strong>
          {showTag ? check.label : ''}
          {check.note && (
            <span
              style={{
                color: token.colorTextQuaternary,
                marginLeft: 4,
              }}
            >
              ({check.note})
            </span>
          )}
        </Typography.Text>

        {check.value !== undefined || check.threshold !== undefined ? (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
            }}
          >
            {check.value !== undefined && (
              <Typography.Text
                strong
                style={{
                  color: check.passed ? token.colorSuccess : token.colorError,
                  minWidth: 32,
                  textAlign: 'right',
                }}
              >
                {check.value == null ? '0' : check.value}
              </Typography.Text>
            )}
            {check.threshold !== undefined && (
              <Typography.Text type="secondary">
                / {check.threshold}
              </Typography.Text>
            )}
          </div>
        ) : (
          showTag && (
            <Tag
              size="xsmall"
              color={check.passed ? 'success' : 'error'}
              bordered={false}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                {check.passed ? <TickMarkOutlined /> : <CloseCircleOutlined />}
                {check.passed ? 'Added' : 'Not Added'}
              </div>
            </Tag>
          )
        )}
      </div>

      <Typography.Metadata>{check.description}</Typography.Metadata>

      {!check.passed && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {check.action && (
            <Button
              style={{ width: 'fit-content' }}
              size="small"
              shape="default"
              type="default"
              data-testid={`fix-button-${factorTestId}`}
              onClick={() => {
                onClose();
                check.action!();
              }}
            >
              {`Add ${check.label}`}
            </Button>
          )}
          {check.waivable && !editing && !isWaived && (
            <Button
              size="small"
              shape="default"
              type="default"
              data-testid={`justify-button-${factorTestId}`}
              onClick={() => onEditStart()}
            >
              Raise Exception
            </Button>
          )}
        </div>
      )}

      {check.passed && !check.waivable && check.action && (
        <Button
          style={{ width: 'fit-content' }}
          size="small"
          shape="default"
          type="default"
          onClick={() => {
            onClose();
            check.action!();
          }}
        >
          {`View ${check.label}`}
        </Button>
      )}

      {isWaived && !editing && (
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 6,
            padding: '6px 8px 6px 10px',
            background: token.colorBgLayout,
            borderRadius: 4,
            borderLeft: `3px solid ${token.colorBorder}`,
          }}
        >
          <Typography.Metadata
            type="secondary"
            style={{
              fontStyle: 'italic',
              flex: 1,
              minWidth: 0,
              lineHeight: '18px',
            }}
          >
            &quot;{justification}&quot;
          </Typography.Metadata>
          <Button
            type="text"
            size="small"
            aria-label="Edit justification"
            icon={
              <EditableOutlined
                style={{ fontSize: 14, color: token.colorTextQuaternary }}
              />
            }
            data-testid={`edit-justification-${factorTestId}`}
            onClick={(e) => {
              e.stopPropagation();
              onEditStart();
            }}
            style={{
              flexShrink: 0,
              height: 24,
              width: 24,
              minWidth: 24,
              padding: 0,
            }}
          />
        </div>
      )}

      {editing && (
        <div style={{ marginTop: 10 }}>
          <Input.TextArea
            autoFocus
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="Explain why this guardrail cannot be implemented…"
            autoSize={{ minRows: 2, maxRows: 4 }}
            maxLength={255}
            showCount
            // style={{ fontSize: 12 }}
          />
          <div
            style={{
              display: 'flex',
              gap: 6,
              marginTop: 20,
              justifyContent: 'flex-end',
            }}
          >
            <Button
              size="small"
              type="secondary"
              onClick={() => {
                setDraft(justification ?? '');
                onEditEnd();
              }}
            >
              Cancel
            </Button>
            <Button
              size="small"
              type="primary"
              onClick={() => {
                onJustify?.(draft.trim());
                onEditEnd();
              }}
            >
              Save
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

interface FactorRowHeaderProps {
  factor: StatusFactor;
  justification?: string;
}

const FactorRowHeader = ({ factor, justification }: FactorRowHeaderProps) => {
  const isWaived = !factor.passed && !!justification;
  const isOk = factor.passed || isWaived;
  const factorTestId = factor.key ?? factor.label;

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
      }}
      data-testid={`factor-${factorTestId}`}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <div
          className="factor-row-header-status-bar"
          style={{
            backgroundColor: isOk
              ? isWaived
                ? '#F5F5F5'
                : 'rgba(0, 177, 79, 0.1)'
              : 'rgba(212, 46, 28, 0.1)',
          }}
        />
        <Button
          className="factor-row-header-button-icon"
          style={{
            height: 24,
            width: 24,
            zIndex: 100,
          }}
          size="small"
          shape="default"
          type={isWaived ? 'default' : 'primary'}
          danger={!isOk}
          icon={<TickMarkOutlined />}
        />

        <Typography.Text strong data-testid={`factor-label-${factorTestId}`}>
          {factor.label}
        </Typography.Text>
      </div>
    </div>
  );
};

interface FactorRowBodyProps {
  factor: StatusFactor;
  justification?: string;
  onClose: () => void;
  onJustify?: (text: string) => void;
  onEditStart: () => void;
  onEditEnd: () => void;
  isEditing: boolean;
}

const FactorRowBody = ({
  factor,
  justification,
  onClose,
  onJustify,
  onEditStart,
  onEditEnd,
  isEditing: editing,
}: FactorRowBodyProps) => {
  const { token } = theme.useToken();
  const [draft, setDraft] = useState(justification ?? '');

  const isWaived = !factor.passed && !!justification;
  const isOk = factor.passed || isWaived;
  const factorTestId = factor.key ?? factor.label;

  return (
    <div
      style={{
        padding: '0 12px 0 4px',
        display: 'flex',
        height: 'fit-content',
        flexDirection: 'column',
        gap: 8,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        {factor.subChecks.length > 0 && (
          <div
            style={{
              backgroundColor: isOk
                ? isWaived
                  ? '#F5F5F5'
                  : 'rgba(0, 177, 79, 0.1)'
                : 'rgba(212, 46, 28, 0.1)',
              width: 32,
              alignSelf: 'stretch',
              borderBottomLeftRadius: 8,
              borderBottomRightRadius: 8,
            }}
          />
        )}

        <div
          style={{ display: 'flex', flexDirection: 'column', gap: 12, flex: 1 }}
        >
          {factor.subChecks.map((check) => (
            <SubCheckRow
              key={check.label}
              check={check}
              token={token}
              showTag={factor.subChecks.length > 1}
              onEditStart={() => onEditStart()}
              editing={editing}
              draft={draft}
              setDraft={setDraft}
              justification={justification}
              onEditEnd={onEditEnd}
              onJustify={onJustify}
              isWaived={isWaived}
              factorTestId={factorTestId}
              onClose={onClose}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

interface StatusBodyProps {
  config: StatusTagConfig;
  factors: StatusFactor[];
  justifications: Record<string, string>;
  onJustify?: (factorLabel: string, text: string) => void;
  onClose: () => void;
  dataTestId?: string;
}

const StatusBody = ({
  config,
  factors,
  justifications,
  onJustify,
  onClose,
  dataTestId,
}: StatusBodyProps) => {
  const { token } = theme.useToken();
  const [editingFactorKey, setEditingFactorKey] = useState<string | null>(null);
  const [failingActiveKeys, setFailingActiveKeys] = useState<string[]>(() =>
    factors
      .filter((f) => {
        const key = f.key ?? f.label;
        return !f.passed && !justifications[key];
      })
      .map((f) => f.key ?? f.label),
  );

  const handleFailingEditStart = (key: string) => {
    setEditingFactorKey(key);
    setFailingActiveKeys((prev) =>
      prev.includes(key) ? prev : [...prev, key],
    );
  };

  const handlePassingEditStart = (key: string) => {
    setEditingFactorKey(key);
  };

  const handleEditEnd = () => setEditingFactorKey(null);

  const items = factors.map((f) => {
    const key = f.key ?? f.label;
    return {
      key,
      label: <FactorRowHeader factor={f} justification={justifications[key]} />,
      children: (
        <FactorRowBody
          factor={f}
          justification={justifications[key]}
          onClose={onClose}
          onJustify={(text) => onJustify?.(key, text)}
          onEditStart={() =>
            !f.passed && !justifications[key]
              ? handleFailingEditStart(key)
              : handlePassingEditStart(key)
          }
          onEditEnd={handleEditEnd}
          isEditing={editingFactorKey === key}
        />
      ),
      styles: {
        header: {
          background: 'transparent',
        },
        body: {
          padding: 0,
          paddingBottom: 4,
        },
      },
    };
  });

  return (
    <div data-testid={dataTestId}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {items?.length && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {items.map((item) => (
              <Collapse
                size="small"
                key={item.key}
                expandIconPosition="end"
                activeKey={failingActiveKeys}
                onChange={(keys) =>
                  setFailingActiveKeys(
                    Array.isArray(keys) ? (keys as string[]) : [keys as string],
                  )
                }
                className="factor-row-container"
                items={[item]}
                style={{ background: '#FFF' }}
              />
            ))}
          </div>
        )}

        <div
          style={{
            padding: '10px 14px',
            background: token.colorFillQuaternary,
            borderRadius: 6,
            border: `1px solid ${token.colorBorder}`,
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 6,
            }}
          >
            <WarningFilled
              style={{
                color: token.colorTextQuaternary,
                fontSize: 12,
                marginTop: 2,
                flexShrink: 0,
              }}
            />
            <Typography.Metadata type="secondary">
              {config.footerNote}{' '}
              <Typography.Link
                href={config.docLink}
                target="_blank"
                style={{ fontSize: 11 }}
              >
                Learn more ↗
              </Typography.Link>
              {config.reduceLink && (
                <>
                  {' · '}
                  <Typography.Link
                    href={config.reduceLink}
                    target="_blank"
                    style={{ fontSize: 11 }}
                  >
                    How to reduce →
                  </Typography.Link>
                </>
              )}
              {' · Reach out to the PMK team on '}
              <Typography.Link
                href={config.slackLink}
                target="_blank"
                style={{ fontSize: 11 }}
              >
                {config.slackLabel}
              </Typography.Link>
              .
            </Typography.Metadata>
          </div>
        </div>
      </div>
    </div>
  );
};

interface ComplexityTagProps {
  config: StatusTagConfig;
  factors: StatusFactor[];
  justifications?: Record<string, string>;
  onJustify?: (factorLabel: string, text: string) => void;
  dataTestId?: string;
}

export const ComplexityTag = ({
  config,
  factors,
  justifications = {},
  onJustify,
  dataTestId,
}: ComplexityTagProps) => {
  const [modalOpen, setModalOpen] = useState(false);

  const isPassing = factors.every((f) => {
    const key = f.key ?? f.label;
    return f.passed || !!justifications[key];
  });
  const failColor = config.failColor ?? 'error';

  const failingNames = factors
    .filter((f) => {
      const key = f.key ?? f.label;
      return !f.passed && !justifications[key];
    })
    .map((f) => f.label);

  const tooltipTitle = isPassing
    ? `All ${config.tagLabel.toLowerCase()} checks pass`
    : `Needs attention: ${failingNames.join(', ')}`;

  return (
    <>
      <Tooltip title={tooltipTitle} placement="top" mouseEnterDelay={0.4}>
        <span
          data-testid={dataTestId ? `${dataTestId}-trigger` : undefined}
          style={{ cursor: 'pointer' }}
          onClick={() => setModalOpen(true)}
          role="button"
        >
          <Tag
            size="xsmall"
            color={isPassing ? 'success' : failColor}
            bordered={false}
            style={{ userSelect: 'none' }}
          >
            {isPassing ? config.passLabel : config.failLabel}
            <WarningFilled
              style={{ marginLeft: 4, fontSize: 10, opacity: 0.7 }}
            />
          </Tag>
        </span>
      </Tooltip>

      <Modal
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        destroyOnClose
        footer={null}
        width={600}
        centered
        title={
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
            }}
          >
            <Typography.Text
              strong
              style={{ fontSize: 18 }}
              data-testid={dataTestId ? `${dataTestId}-label` : undefined}
            >
              {config.tagLabel}
            </Typography.Text>
            <Tag
              size="xsmall"
              color={isPassing ? 'success' : failColor}
              bordered={false}
              style={{ fontWeight: 600 }}
            >
              {isPassing ? config.passLabel : config.failLabel}
              <WarningFilled
                style={{ marginLeft: 4, fontSize: 10, opacity: 0.7 }}
              />
            </Tag>
          </div>
        }
        styles={{ body: { paddingTop: 8 } }}
      >
        <StatusBody
          config={config}
          factors={factors}
          justifications={justifications}
          onJustify={onJustify}
          onClose={() => setModalOpen(false)}
          dataTestId={dataTestId}
        />
      </Modal>
    </>
  );
};

export default ComplexityTag;
